import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material'
import {
  ExpandMore,
  CheckCircle,
  Error,
  Warning,
  Refresh,
} from '@mui/icons-material'
import { TraySubmissionSummary } from './TraySubmission.types'

interface TraySubmissionResultsProps {
  open: boolean
  onClose: () => void
  onRetryFailed: () => void
  onNavigateToManagement: () => void
  summary: TraySubmissionSummary | null
  isRetrying?: boolean
}

const TraySubmissionResults: React.FC<TraySubmissionResultsProps> = ({
  open,
  onClose,
  onRetryFailed,
  onNavigateToManagement,
  summary,
  isRetrying = false,
}) => {
  if (!summary) return null

  const { successful, failed, successCount, failureCount, totalTrays } = summary

  const getAlertVariant = () => {
    if (failureCount === 0) return 'success'
    if (successCount === 0) return 'error'
    return 'warning'
  }

  const getTitle = () => {
    if (failureCount === 0) return 'All Trays Submitted Successfully!'
    if (successCount === 0) return 'Tray Submission Failed'
    return 'Partial Success - Some Trays Failed'
  }

  const getMessage = () => {
    if (failureCount === 0) {
      return `All ${totalTrays} tray${
        totalTrays > 1 ? 's' : ''
      } have been successfully submitted to the database.`
    }
    if (successCount === 0) {
      return `Failed to submit all ${totalTrays} tray${
        totalTrays > 1 ? 's' : ''
      }. Please review the errors below and try again.`
    }
    return `${successCount} of ${totalTrays} trays were submitted successfully. ${failureCount} tray${
      failureCount > 1 ? 's' : ''
    } failed to submit.`
  }

  return (
    <Dialog
      open={open}
      onClose={failureCount === 0 ? onClose : undefined}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={failureCount > 0}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {failureCount === 0 ? (
            <CheckCircle color="success" />
          ) : successCount === 0 ? (
            <Error color="error" />
          ) : (
            <Warning color="warning" />
          )}
          <Typography variant="h6">{getTitle()}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity={getAlertVariant()} sx={{ mb: 3 }}>
          {getMessage()}
        </Alert>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="h4" color="text.secondary">
                {totalTrays}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Trays
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="h4" color="success.main">
                {successCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Successful
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box textAlign="center">
              <Typography variant="h4" color="error.main">
                {failureCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Failed
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {successCount > 0 && (
          <Accordion defaultExpanded={failureCount === 0}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" color="success.main">
                Successfully Submitted Trays ({successCount})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {successful.map((result) => (
                  <ListItem key={result.trayId}>
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={result.description}
                      secondary={`Barcode: ${result.barcode} | ID: ${result.createdTrayId}`}
                    />
                    <Chip
                      label={`${result.attempts} attempt${
                        result.attempts > 1 ? 's' : ''
                      }`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}

        {failureCount > 0 && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" color="error.main">
                Failed Submissions ({failureCount})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {failed.map((result) => (
                  <ListItem key={result.trayId}>
                    <ListItemIcon>
                      <Error color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={result.description}
                      secondary={
                        <>
                          <Typography variant="body2" component="span">
                            Barcode: {result.barcode}
                          </Typography>
                          <br />
                          <Typography
                            variant="body2"
                            component="span"
                            color="error"
                          >
                            Error: {result.error}
                          </Typography>
                        </>
                      }
                    />
                    <Chip
                      label={`${result.attempts} attempt${
                        result.attempts > 1 ? 's' : ''
                      }`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {failureCount > 0 && (
          <Button
            onClick={onRetryFailed}
            variant="outlined"
            startIcon={<Refresh />}
            disabled={isRetrying}
          >
            {isRetrying ? 'Retrying...' : 'Retry Failed Trays'}
          </Button>
        )}

        {successCount > 0 && (
          <Button
            onClick={onNavigateToManagement}
            variant="contained"
            color="primary"
          >
            Go to Tray Management
          </Button>
        )}

        {failureCount === 0 && (
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default TraySubmissionResults
